import React, { useState, useContext } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";

import { ManageTableViewDialog } from "components/CasesTable/ManageTableViewDialog";
import { CreateTableViewDialog } from "components/CasesTable/CreateTableViewDialog";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { CaseManagementParamsContext } from "contexts/CaseManagementParamsContext";
import { useLazyFetchGet } from "hooks/fetch/useLazyFetchGet";

import {
  CASE_TABLE_COLUMNS,
  CONSTANT_HIDDEN_COLUMNS,
  DEFAULT_SORT,
  INITIAL_HIDDEN_COLUMNS,
  CONDITIONALLY_HIDDEN_COLUMNS
} from "components/CasesTable/CaseManagementTableColumns";

import { styled } from "@mui/material/styles";

export function CaseTableOptions() {
  const [preferences, setPreferences] = useState([]);
  const [manageViewsOpen, setManageViewsOpen] = useState(false);
  const [createViewOpen, setCreateViewOpen] = useState(false);

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const {
    DEFAULT_ROWS_PER_PAGE,
    GROUPING_ROWS_PER_PAGE,
    tableState,
    setTableState,
    savedTableView,
    onSetSavedTableView,
    fetchCases
  } = useContext(CaseManagementParamsContext);

  const onCompleted = data => {
    setPreferences(data?.results ?? []);
  };

  const onError = () => {
    addSnackbarToQueue({
      message: "Case Management table views failed to load.",
      severity: "error"
    });
  };

  const { loading, error, getCallback } = useLazyFetchGet(
    "case_management_preferences?ordering=name",
    onCompleted,
    onError
  );

  const loadOptions = () => {
    if (!loading && !error && preferences.length === 0) {
      getCallback();
    }
  };

  const onCurrentPreferenceSet = (_event, value) => {
    let defaultHiddencolumns = [];
    const preference = preferences.find(({ name }) => name === value) ?? null;
    onSetSavedTableView(preference);

    if (!preference) {
      const columnIds = CASE_TABLE_COLUMNS.map(({ id }) => id);
      defaultHiddencolumns = tableState.columns
        .filter(
          ({ id }) =>
            !columnIds.includes(id) ||
            INITIAL_HIDDEN_COLUMNS.includes(id) ||
            CONDITIONALLY_HIDDEN_COLUMNS.includes(id)
        )
        .map(({ id }) => id);
    }

    setTableState({
      ...tableState,
      filters: preference?.params?.filters ?? {},
      sortBy: preference?.params?.sortBy ?? [DEFAULT_SORT],
      hiddenColumns: preference?.params?.hiddenColumns ?? [
        ...defaultHiddencolumns,
        ...CONSTANT_HIDDEN_COLUMNS
      ],
      columnOrder: preference?.params?.columnOrder ?? [],
      groupBy: preference?.params?.groupBy ?? [],
      rowsPerPage:
        preference?.params?.groupBy?.length > 0
          ? GROUPING_ROWS_PER_PAGE
          : DEFAULT_ROWS_PER_PAGE,
      pageIndex: 0
    });

    fetchCases(true);
  };

  const onSetManageTableViewsOpen = () => {
    loadOptions();
    setManageViewsOpen(true);
  };

  return (
    <>
      <OptionsContainer>
        <OptionsDropdown
          id="table-view-list"
          name="table-views"
          size="small"
          autoComplete
          autoHighlight
          onFocus={loadOptions}
          options={
            preferences.length !== 0
              ? preferences.map(prefs => prefs.name)
              : ["Loading..."]
          }
          getOptionDisabled={option => option === "Loading..."}
          value={savedTableView?.name || null}
          onChange={(event, value) => onCurrentPreferenceSet(event, value)}
          renderInput={params => (
            <TextField
              {...params}
              label="Table Views"
              variant="outlined"
              InputProps={{ ...params.InputProps }}
            />
          )}
        />
        <div>
          <Link
            component="button"
            variant="body2"
            data-qa="CM-Button-Save-Manage-Table-Views"
            onClick={onSetManageTableViewsOpen}
          >
            Save / Manage table views
          </Link>
        </div>
      </OptionsContainer>
      <ManageTableViewDialog
        open={manageViewsOpen}
        setOpen={setManageViewsOpen}
        preferences={preferences}
        refetch={getCallback}
        setCreateViewOpen={setCreateViewOpen}
      />
      <CreateTableViewDialog
        preferences={preferences}
        open={createViewOpen}
        setOpen={setCreateViewOpen}
        refetch={getCallback}
      />
    </>
  );
}

const OptionsContainer = styled("div")`
  width: fit-content;
  display: inline-block;
`;

const OptionsDropdown = styled(Autocomplete)`
  min-width: 200px;
  display: inline-flex;
`;
